import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type PointerEvent as EventoPuntero } from 'react';
import { createPortal } from 'react-dom';

/**
 * El icono de ayuda "?".
 *
 * Antes usaba `title` nativo del navegador: tarda ~1 s en aparecer y en un
 * móvil/tablet (sin hover) NO aparece nunca — de ahí el "los ? no funcionan"
 * de Alex. Este muestra un globo de verdad con hover Y con toque/click, que es
 * lo que hace falta en táctil. Se cierra al tocar fuera o con Escape.
 *
 * El globo se pinta en un PORTAL, con posición fija calculada desde el botón.
 * Cuando iba absoluto dentro del `?` lo recortaba cualquier antepasado con
 * scroll: en la tabla de Precios de la carta, el de "Coste de referencia"
 * salía hacia arriba y quedaba cortado por el contenedor, así que no había
 * forma de leerlo (Alex, 1 sep 2026). Fijo y fuera del árbol de la tabla, no
 * lo recorta nadie, y además se da la vuelta solo si no cabe arriba.
 */

/** Separación entre el botón y el globo, y margen mínimo con el borde. */
const HUECO = 8;

const ENFOCABLES = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** El siguiente enfocable de la página tras `desde`, sin contar lo que hay dentro de `salvo`. */
function siguienteEnfocable(desde: HTMLElement, salvo: HTMLElement | null): HTMLElement | null {
  const todos = [...document.querySelectorAll<HTMLElement>(ENFOCABLES)].filter(
    (el) => !salvo?.contains(el) && el.tabIndex >= 0 && el.getClientRects().length > 0,
  );
  const i = todos.indexOf(desde);
  return i >= 0 ? todos[i + 1] ?? null : null;
}

interface Posicion {
  top: number;
  left: number;
  /** El globo va debajo del botón: la flecha tiene que apuntar hacia arriba. */
  debajo: boolean;
}

export function InfoHint({ text, label, detalle, nombre, alPreguntar }: {
  text: string;
  label?: string;
  /** Segunda línea, aparte (el número de la persona en el «?» de QueEs, 15 oct 2026). */
  detalle?: string;
  /** Nombre accesible del botón cuando no es el texto (p. ej. «¿Qué es el prime cost?»). */
  nombre?: string;
  /**
   * «Pregúntale a Alexia» al final del globo (Alex, 15 oct 2026): abre el
   * chat con la pregunta. Sin él, el globo es solo el texto.
   */
  alPreguntar?: () => void;
}): JSX.Element {
  const [abierto, setAbierto] = useState(false);
  const [pos, setPos] = useState<Posicion | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const botonRef = useRef<HTMLButtonElement>(null);
  const globoRef = useRef<HTMLSpanElement>(null);
  const alexiaRef = useRef<HTMLButtonElement>(null);
  /*
   * Escritorio: se abre al pasar el ratón o con el foco; teléfono: al tocar
   * (Alex, 15 oct 2026). En táctil el toque también da el foco (y en algunos
   * navegadores un «mouseenter» de mentira) justo antes del click: si el
   * click llega pegado a esa apertura, no la deshace.
   */
  const abiertoEn = useRef(0);
  // El estado también en una ref: el foco y el click del toque llegan en el
  // mismo tic, antes de que React vuelva a pintar.
  const abiertoAhora = useRef(false);
  const cierre = useRef<number | undefined>(undefined);
  const id = useId();

  const poner = useCallback((v: boolean) => {
    abiertoAhora.current = v;
    setAbierto(v);
  }, []);
  const abrir = useCallback(() => {
    window.clearTimeout(cierre.current);
    if (!abiertoAhora.current) abiertoEn.current = Date.now();
    poner(true);
  }, [poner]);
  // Con un respiro: así da tiempo a llevar el ratón del «?» al globo (y a
  // «Pregúntale a Alexia») sin que se cierre por el camino.
  const cerrarEnUnRato = useCallback(() => {
    window.clearTimeout(cierre.current);
    cierre.current = window.setTimeout(() => poner(false), 180);
  }, [poner]);
  useEffect(() => () => window.clearTimeout(cierre.current), []);

  const recolocar = useCallback(() => {
    const boton = botonRef.current;
    const globo = globoRef.current;
    if (!boton || !globo) return;

    const b = boton.getBoundingClientRect();
    const g = globo.getBoundingClientRect();
    // Arriba si cabe; si no, debajo. Es lo que pasa en la primera fila de una
    // tabla, que es justo donde estan las cabeceras con ayuda.
    const debajo = b.top < g.height + HUECO * 2;
    const top = debajo ? b.bottom + HUECO : b.top - g.height - HUECO;
    /*
     * Centrado en el boton, pero sin salirse por los lados. El orden importa:
     * el minimo se aplica AL FINAL para que, si la ventana es mas estrecha que
     * el globo, quede pegado al borde izquierdo y no fuera de la pantalla.
     */
    const ideal = b.left + b.width / 2 - g.width / 2;
    const left = Math.max(HUECO, Math.min(ideal, window.innerWidth - g.width - HUECO));
    setPos({ top, left, debajo });
  }, []);

  useLayoutEffect(() => {
    if (abierto) recolocar();
    else setPos(null);
  }, [abierto, recolocar]);

  useEffect(() => {
    if (!abierto) return;
    // El globo va en un portal: «dentro» es el «?» o el globo.
    const dentro = (n: Node | null) => Boolean(n && (ref.current?.contains(n) || globoRef.current?.contains(n)));
    const fuera = (e: MouseEvent) => {
      if (!dentro(e.target as Node)) poner(false);
    };
    const escape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') poner(false);
    };
    // Al ser fijo, el globo no acompanya al scroll: se cierra, que es lo que
    // espera cualquiera que empieza a moverse por la pagina.
    // Salvo el scroll que trae el propio toque (el navegador acerca el «?»
    // al tocarlo, o el teclado del teléfono): en ese momento solo se recoloca.
    const cerrar = () => {
      if (Date.now() - abiertoEn.current < 400) recolocar();
      else poner(false);
    };
    document.addEventListener('mousedown', fuera);
    document.addEventListener('keydown', escape);
    window.addEventListener('scroll', cerrar, true);
    window.addEventListener('resize', cerrar);
    return () => {
      document.removeEventListener('mousedown', fuera);
      document.removeEventListener('keydown', escape);
      window.removeEventListener('scroll', cerrar, true);
      window.removeEventListener('resize', cerrar);
    };
  }, [abierto, poner, recolocar]);

  const conRaton = (e: EventoPuntero) => e.pointerType === 'mouse';
  const fueraDelHint = (n: EventTarget | null) =>
    !(n instanceof Node) || !(ref.current?.contains(n) || globoRef.current?.contains(n));

  return (
    <span
      ref={ref}
      className={`infohint${label ? ' infohint--label' : ''}`}
      onPointerEnter={(e) => conRaton(e) && abrir()}
      onPointerLeave={(e) => conRaton(e) && cerrarEnUnRato()}
    >
      <button
        ref={botonRef}
        type="button"
        className={label ? 'btn btn--ghost btn--sm' : 'infohint__btn'}
        aria-label={nombre ?? (detalle ? `${text} ${detalle}` : text)}
        aria-expanded={abierto}
        aria-describedby={abierto ? id : undefined}
        onFocus={abrir}
        onBlur={(e) => fueraDelHint(e.relatedTarget) && poner(false)}
        onKeyDown={(e) => {
          // Dentro de una fila que se abre con Intro (la carta): el «?» no la abre.
          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
          // El globo está en un portal, fuera del orden del tabulador: Tab
          // desde el «?» abierto lleva a «Pregúntale a Alexia».
          if (e.key === 'Tab' && !e.shiftKey && abierto && alexiaRef.current) {
            e.preventDefault();
            alexiaRef.current.focus();
          }
        }}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          window.clearTimeout(cierre.current);
          // Recién abierto por el foco o el ratón (el toque los da justo antes): se queda abierto.
          if (abiertoAhora.current && Date.now() - abiertoEn.current < 500) return;
          if (abiertoAhora.current) poner(false);
          else abrir();
        }}
      >
        {label ?? '?'}
      </button>
      {abierto &&
        createPortal(
          <span
            ref={globoRef}
            className={`infohint__bubble ${pos?.debajo ? 'infohint__bubble--debajo' : ''}`.trim()}
            role="tooltip"
            id={id}
            onPointerEnter={(e) => conRaton(e) && abrir()}
            onPointerLeave={(e) => conRaton(e) && cerrarEnUnRato()}
            // Mientras no se ha medido va invisible: si no, se ve saltar del
            // sitio de partida al definitivo.
            style={
              pos
                ? { top: `${pos.top}px`, left: `${pos.left}px` }
                : { top: 0, left: 0, visibility: 'hidden' }
            }
          >
            {text}
            {detalle && <span className="infohint__detalle">{detalle}</span>}
            {alPreguntar && (
              <button
                ref={alexiaRef}
                type="button"
                className="infohint__alexia"
                onBlur={(e) => fueraDelHint(e.relatedTarget) && poner(false)}
                onKeyDown={(e) => {
                  // El globo vive al final de <body>: sin esto, Tab manda el foco
                  // a BODY y Shift+Tab salta a lo último de la página (bugbot
                  // 15 oct 2026). Shift+Tab vuelve al «?»; Tab cierra el globo
                  // y sigue por lo que hay después del «?».
                  if (e.key !== 'Tab') return;
                  const boton = botonRef.current;
                  if (!boton) return;
                  e.preventDefault();
                  if (e.shiftKey) {
                    boton.focus();
                    return;
                  }
                  const siguiente = siguienteEnfocable(boton, globoRef.current);
                  poner(false);
                  if (siguiente) siguiente.focus();
                  else boton.focus();
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  poner(false);
                  alPreguntar();
                }}
              >
                Pregúntale a Alexia
              </button>
            )}
          </span>,
          document.body,
        )}
    </span>
  );
}
